// src/hooks/useCuratorDecisions.ts
//
// Live subscription to the curator's decision log:
// /users/{uid}/curator_decisions/ ordered by triggered_at desc.
//
// This is the read-side that powers the Memory log UI. Every
// curator decision (create, merge, supersede, annotate, discard,
// promote_pending) appears here as soon as it's written.
//
// We pass `pageSize` to control how many decisions to show. The
// returned `loadMore` bumps the limit; useful for "show older
// decisions" without going to a separate paginated query.
//
// This hook is read-only. Forget / Confirm actions on individual
// memories are out of scope here — they'd live in a separate hook
// against the /memories/ collection (Phase 3D scope per
// CURATOR_NOTES.md "What's NOT in 3B-curator").

import { useEffect, useMemo, useState } from 'react';
import {
  collection,
  query,
  orderBy,
  limit,
  onSnapshot,
  Timestamp,
} from 'firebase/firestore';
import { auth, db } from '../services/firebase';

export type CuratorAction =
  | 'create'
  | 'merge'
  | 'supersede'
  | 'annotate'
  | 'discard'
  | 'promote_pending';

export type TriggerKind =
  | 'chat_turn'
  | 'photo_annotation'
  | 'heartbeat'
  | 'user_statement'
  | 'profile_seed'
  | 'message_bookmark'
  | 'guide_card_heart'
  | 'taste_consolidation';

export type CuratorRunStatus =
  | 'ok'
  | 'parse_failed'
  | 'schema_invalid'
  | 'quote_unverified_downgraded'
  | 'timeout';

/** Projection of /users/{uid}/curator_decisions/{id} into the
 *  shape the UI consumes. We pull the fields the cards render
 *  and skip the larger `context_snapshot` and `retrieval_queries`
 *  arrays unless the user expands a card (in which case the card
 *  reads them lazily on demand). */
export interface CuratorDecisionRow {
  id: string;
  triggered_at: Date | null;
  trigger_kind: TriggerKind;
  decision: CuratorAction;
  candidate_text: string;
  final_text: string;
  rationale: string;
  source_kind: string;
  half_life_hint: string;
  uncertainties: string[];
  retrieved_neighbor_count: number;
  applied_writes_count: number;
  created_memory_id: string | null;
  evidence_quote: string | null;
  curator_run_status: CuratorRunStatus | null;
  duration_ms: number | null;
  curator_model: string | null;
}

const DEFAULT_PAGE_SIZE = 50;

export function useCuratorDecisions(initialPageSize: number = DEFAULT_PAGE_SIZE) {
  const [decisions, setDecisions] = useState<CuratorDecisionRow[]>([]);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const user = auth.currentUser;
    if (!user) {
      setIsLoading(false);
      setDecisions([]);
      return;
    }

    setIsLoading(true);
    const ref = collection(db, 'users', user.uid, 'curator_decisions');
    const q = query(ref, orderBy('triggered_at', 'desc'), limit(pageSize));

    const unsub = onSnapshot(
      q,
      (snapshot) => {
        const out: CuratorDecisionRow[] = [];
        snapshot.forEach((docSnap) => {
          const d = docSnap.data() as Record<string, unknown>;
          out.push({
            id: docSnap.id,
            triggered_at:
              d.triggered_at instanceof Timestamp
                ? d.triggered_at.toDate()
                : null,
            trigger_kind:
              ((d.triggered_by as { kind?: string } | undefined)?.kind as TriggerKind) ||
              ('chat_turn' as TriggerKind),
            decision: (d.decision as CuratorAction) || 'discard',
            candidate_text: typeof d.candidate_text === 'string' ? d.candidate_text : '',
            final_text:
              typeof (d as any).final_text === 'string'
                ? (d as any).final_text
                : '',
            rationale: typeof d.rationale === 'string' ? d.rationale : '',
            source_kind:
              typeof (d as any).source_kind === 'string'
                ? (d as any).source_kind
                : '',
            half_life_hint:
              typeof (d as any).half_life_hint === 'string'
                ? (d as any).half_life_hint
                : '',
            uncertainties: Array.isArray(d.uncertainties)
              ? (d.uncertainties as unknown[]).filter(
                  (s): s is string => typeof s === 'string'
                )
              : [],
            retrieved_neighbor_count: Array.isArray(d.retrieved_neighbors)
              ? (d.retrieved_neighbors as unknown[]).length
              : 0,
            applied_writes_count: Array.isArray(d.applied_writes)
              ? (d.applied_writes as unknown[]).length
              : 0,
            created_memory_id:
              typeof (d as any).created_memory_id === 'string'
                ? (d as any).created_memory_id
                : null,
            evidence_quote:
              typeof (d as any).evidence_quote === 'string'
                ? (d as any).evidence_quote
                : null,
            curator_run_status:
              typeof (d as any).curator_run_status === 'string'
                ? ((d as any).curator_run_status as CuratorRunStatus)
                : null,
            duration_ms:
              typeof d.duration_ms === 'number' ? d.duration_ms : null,
            curator_model:
              typeof d.curator_model === 'string' ? d.curator_model : null,
          });
        });
        setDecisions(out);
        setIsLoading(false);
        setError(null);
      },
      (err) => {
        console.warn('curator_decisions listener error:', err);
        setError(err.message || 'Subscription error');
        setIsLoading(false);
      }
    );

    return () => unsub();
  }, [pageSize]);

  const loadMore = useMemo(
    () => () => setPageSize((s) => s + DEFAULT_PAGE_SIZE),
    []
  );

  return { decisions, isLoading, error, loadMore, pageSize };
}
